import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { getSiteUrl } from '@/utils/seo';

export default function SafetyArticlePage() {
  const { t } = useTranslation();
  const siteUrl = getSiteUrl();

  const safetySchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t('safety.title'),
    description: t('safety.meta_desc'),
    image: 'https://readdy.ai/api/search-image?query=Clean%20conceptual%20photography%20of%20a%20brass%20padlock%20resting%20on%20a%20stack%20of%20neatly%20organized%20documents%20and%20receipts%20on%20a%20warm%20wooden%20surface%2C%20soft%20natural%20side%20lighting%20creating%20gentle%20dimensional%20shadows%2C%20trust%20and%20security%20visual%20metaphor%20with%20warm%20golden%20brass%20tones%2C%20minimalist%20financial%20safety%20aesthetic&width=600&height=450&seq=kg-art-card3-v2&orientation=landscape',
    datePublished: '2026-07-07',
    dateModified: '2026-07-07',
    author: {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/bezpieczne-zakupy-kakobuy',
    },
    inLanguage: 'pl',
  };

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={safetySchema} />
      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-4xl mx-auto px-4 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg font-bold text-foreground-900 tracking-tight whitespace-nowrap">
            kakobuyfinds<span className="text-primary-500">.website</span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link
              to="/artykul/kakobuy-sheet-poradnik"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-text-line"></i>
              {t('nav.article')}
            </Link>
            <Link
              to="/poradnik-zakupowy-kakobuy"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              {t('nav.guide')}
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.back_home')}
            </Link>
          </nav>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 md:px-10 py-12 md:py-16">
        <header className="mb-10">
          <div className="flex items-center gap-4 text-sm text-foreground-500 mb-4">
            <time dateTime="2026-07-07">{t('safety.date')}</time>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>{t('safety.author')}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
            {t('safety.title')}
          </h1>
        </header>

        <div className="space-y-8">
          <section>
            <p className="text-lg text-foreground-700 leading-relaxed">
              {t('safety.intro')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('safety.section1_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section1_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('safety.section1_p2')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('safety.section2_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section2_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section2_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('safety.section2_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('safety.section3_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section3_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section3_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('safety.section3_p3')}
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('safety.section4_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section4_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.section4_p2')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('safety.section4_p3')}
            </p>
          </section>

          <section className="bg-background-100 rounded-2xl p-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              {t('safety.conclusion_title')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              {t('safety.conclusion_p1')}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {t('safety.conclusion_p2')}{' '}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline">
                {t('safety.conclusion_link')}
              </Link>
              {t('safety.conclusion_suffix')}
            </p>
          </section>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              {t('article.back_home')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </article>

      <footer className="border-t border-background-200 py-8">
        <div className="max-w-3xl mx-auto px-4 md:px-10 text-center">
          <Link to="/" className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
            kakobuyfinds<span className="text-primary-500">.website</span>
          </Link>
          <p className="text-xs text-foreground-500 mt-2">© 2026 kakobuyfinds.website. Wszelkie prawa zastrzeżone.</p>
        </div>
      </footer>
    </main>
  );
}